import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Checkbox,
    Box,
    Chip,
    Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const InvoiceMobileView = ({
    invoiceData,
    page,
    rowsPerPage,
    selectedItems,
    onSelectItem,
    onInvoicePreviewById,
    t
}) => {
    const getStatusColor = (status) => {
        const statusColors = {
            'issued': 'primary',
            'canceled': 'error',
            'completed': 'success',
            'paid': 'success',
            'partial_paid': 'warning',
            'overdue': 'error'
        };
        return statusColors[status] || 'default';
    };

    const getStatusText = (status) => {
        const statusMap = {
            'issued': t('invoice.statusOptions.issued'),
            'canceled': t('invoice.statusOptions.canceled'),
            'completed': t('invoice.statusOptions.completed'),
            'paid': t('invoice.statusOptions.paid'),
            'partial_paid': t('invoice.statusOptions.partial_paid'),
            'overdue': t('invoice.statusOptions.overdue')
        };
        return statusMap[status] || status;
    };

    const getPaymentStatusColor = (status) => {
        const statusColors = {
            'paid': 'success',
            'unpaid': 'error',
            'partial_paid': 'warning',
            'partial': 'warning',
            'overdue': 'error',
            'issued': 'error',
            'canceled': 'error'
        };
        return statusColors[status] || 'default';
    };

    const getPaymentStatusText = (status) => {
        const statusMap = {
            'paid': t('invoice.statusOptions.paid'),
            'unpaid': t('invoice.statusOptions.unpaid'),
            'partial_paid': t('invoice.statusOptions.partial_paid'),
            'partial': t('invoice.statusOptions.partial_paid'),
            'overdue': t('invoice.statusOptions.overdue'),
            'issued': t('invoice.statusOptions.unpaid'),
            'canceled': t('invoice.statusOptions.canceled')
        };
        return statusMap[status] || status;
    };

    const formatCurrency = (amount) => {
        return amount ? Number(amount).toLocaleString() : '-';
    };

    if (invoiceData.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                    {t('invoice.noData')}
                </Typography>
            </Box>
        );
    }

    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const displayData = invoiceData.slice(startIndex, endIndex);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {displayData.map((item) => (
                <Card key={item.invoice_id} sx={{ width: '100%' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Checkbox
                                checked={selectedItems.includes(item.invoice_id)}
                                onChange={() => onSelectItem(item.invoice_id)}
                                sx={{ mt: 0 }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    {onInvoicePreviewById &&
                                    item.invoice_id != null &&
                                    Number.isFinite(Number(item.invoice_id)) ? (
                                        <Typography
                                            variant="h6"
                                            component="button"
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                onInvoicePreviewById(item.invoice_id);
                                            }}
                                            sx={{
                                                m: 0,
                                                p: 0,
                                                border: 'none',
                                                background: 'none',
                                                font: 'inherit',
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                                color: 'primary.main',
                                                textDecoration: 'underline',
                                                '&:hover': { color: 'primary.dark' }
                                            }}
                                        >
                                            {item.invoice_no || '-'}
                                        </Typography>
                                    ) : (
                                        <Typography variant="h6" component="div">
                                            {item.invoice_no || '-'}
                                        </Typography>
                                    )}
                                    <Chip
                                        label={getStatusText(item.status)}
                                        color={getStatusColor(item.status)}
                                        variant={item.status === 'issued' ? 'outlined' : 'filled'}
                                        size="small"
                                        sx={{
                                            fontWeight: 'bold',
                                            minWidth: '70px',
                                            '& .MuiChip-label': {
                                                fontSize: '0.7rem'
                                            }
                                        }}
                                    />
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.customerName')}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {item.customer_name || '-'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.managerName')}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {item.manager_name || '-'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.invoiceDate')}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {item.invoice_date || '-'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.dueDate')}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {item.due_date || '-'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.currency')}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {item.currency || '-'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.amount')}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatCurrency(item.amount)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.vatAmount')}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {(() => {
                                                const vat1 = Number(item.vat_amount) || 0;
                                                const vat2 = Number(item.vat_amount_2) || 0;
                                                const totalVat = vat1 + vat2;
                                                return formatCurrency(totalVat);
                                            })()}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.totalAmount')}:
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatCurrency(item.total_amount)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.columns.payment')}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatCurrency(item.received_amount)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.status')}:
                                        </Typography>
                                        <Chip
                                            label={getStatusText(item.status)}
                                            color={getStatusColor(item.status)}
                                            variant={item.status === 'issued' ? 'outlined' : 'filled'}
                                            size="small"
                                            sx={{
                                                fontWeight: 'bold',
                                                minWidth: '60px',
                                                '& .MuiChip-label': {
                                                    fontSize: '0.7rem'
                                                }
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('invoice.columns.paymentStatus')}:
                                        </Typography>
                                        <Chip
                                            label={getPaymentStatusText(item.status_account_receivable)}
                                            color={getPaymentStatusColor(item.status_account_receivable)}
                                            variant={item.status_account_receivable === 'paid' ? 'filled' : 'outlined'}
                                            size="small"
                                            sx={{
                                                fontWeight: 'bold',
                                                minWidth: '60px',
                                                '& .MuiChip-label': {
                                                    fontSize: '0.7rem'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};

export default InvoiceMobileView; 